import getConfig from 'next/config';
import { v4 as uuidv4 } from 'uuid';
import contract from '../../lib/utm-observability-contract';
import {
  getMarketingAttributionStorageDiagnostics,
  getStoredMarketingAttribution,
} from './marketing-attribution';

const {
  TRACE_TTL_DAYS,
  analyzeUtmParams,
  buildUtmDiagnostics,
  createTrace,
  parseTrace,
  utmKeyMask,
} = contract;

export const TRACE_COOKIE_BASE_NAME = 'gf_utm_trace_v1';
export const TRACE_STORAGE_BASE_KEY = TRACE_COOKIE_BASE_NAME;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

function getEnvironment() {
  const config = getConfig() || {};
  return config.publicRuntimeConfig?.GF_UTM_OBSERVABILITY_ENV;
}

export function utmObservabilityEndpoint() {
  const config = getConfig() || {};
  const apiEndpoint = config.publicRuntimeConfig?.GF_DOTCOM_API_ENDPOINT;
  const baseUrl = typeof apiEndpoint === 'string' ? apiEndpoint.replace(/\/$/, '') : '';
  return `${baseUrl}/api/utm-observability`;
}

export function traceStorageKey(environment = getEnvironment()) {
  return environment === 'production'
    ? TRACE_STORAGE_BASE_KEY
    : `${TRACE_STORAGE_BASE_KEY}_${environment || 'disabled'}`;
}

function readCookieValue(name) {
  if (!isBrowser()) return null;
  const cookie = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(`${name}=`));
  try {
    return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

function readLocalStorageValue(key) {
  try {
    return window.localStorage?.getItem(key) || null;
  } catch {
    return null;
  }
}

export function readTrace() {
  if (!isBrowser()) return { trace: null, cookieTrace: null, localStorageTrace: null };
  const key = traceStorageKey();
  const cookieTrace = parseTrace(readCookieValue(key));
  const localStorageTrace = parseTrace(readLocalStorageValue(key));
  return { trace: cookieTrace || localStorageTrace, cookieTrace, localStorageTrace };
}

function getCookieDomain() {
  const { hostname } = window.location;
  return hostname === 'glowforge.com' || hostname.endsWith('.glowforge.com')
    ? '.glowforge.com'
    : null;
}

export function persistTrace(trace) {
  if (!isBrowser()) return { cookie: false, localStorage: false };
  const key = traceStorageKey();
  const value = JSON.stringify(trace);
  const expires = new Date(
    Date.now() + TRACE_TTL_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString();
  const cookieParts = [
    `${key}=${encodeURIComponent(value)}`,
    `expires=${expires}`,
    'path=/',
    'SameSite=Lax',
  ];
  const domain = getCookieDomain();
  if (domain) cookieParts.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') cookieParts.push('Secure');

  let localStorageWritten = false;
  try {
    window.localStorage?.setItem(key, value);
    localStorageWritten = readLocalStorageValue(key) === value;
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
  document.cookie = cookieParts.join('; ');
  return {
    cookie: readCookieValue(key) === value,
    localStorage: localStorageWritten,
  };
}

export function getTraceStorageDiagnostics() {
  const { trace, cookieTrace, localStorageTrace } = readTrace();
  return {
    trace_present: Boolean(trace),
    trace_cookie_present: Boolean(cookieTrace),
    trace_local_storage_present: Boolean(localStorageTrace),
    trace_cookie_readback_present: Boolean(cookieTrace),
    trace_local_storage_readback_present: Boolean(localStorageTrace),
  };
}

export function sendUtmEvent(event, trace, properties = {}) {
  if (!trace || !isBrowser() || process.env.NODE_ENV === 'test') return Promise.resolve(false);
  const body = {
    event,
    event_uuid: uuidv4(),
    event_timestamp: new Date().toISOString(),
    trace_id: trace.id,
    properties,
  };
  try {
    return fetch(utmObservabilityEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      keepalive: true,
    }).then((response) => response.ok).catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

function latestTouchDiagnostics() {
  const attribution = getStoredMarketingAttribution();
  return buildUtmDiagnostics(attribution?.latest_touch || {});
}

export function observeUtmPage({
  url = isBrowser() ? window.location.href : '',
  initialPage = false,
} = {}) {
  if (!isBrowser()) return null;
  let { pathname } = window.location;
  try {
    pathname = new URL(url, window.location.href).pathname;
  } catch {
    // Fall back to the current pathname for malformed route notifications.
  }
  const {
    utms,
    keyCaseNormalized,
    keyConflictDetected,
  } = analyzeUtmParams(url, window.location.href);
  let { trace } = readTrace();
  let writeResult = { cookie: false, localStorage: false };

  if (utms) {
    const canReuseServerTrace = initialPage && trace?.origin === 'server'
      && trace.mask === utmKeyMask(utms);
    if (!canReuseServerTrace) trace = createTrace(utms, { origin: 'browser' });
    writeResult = persistTrace(trace);
    sendUtmEvent('utm_pipeline_landing_detected', trace, {
      ...buildUtmDiagnostics(utms, { keyCaseNormalized, keyConflictDetected }),
      stage: 'landing_detected',
      flow: 'unknown',
      trace_origin: trace.origin,
      trace_present: true,
      attribution_present: Boolean(getStoredMarketingAttribution()),
      landing_path: pathname,
    });
    sendUtmEvent('utm_pipeline_storage_checked', trace, {
      ...buildUtmDiagnostics(utms, { keyCaseNormalized, keyConflictDetected }),
      ...getMarketingAttributionStorageDiagnostics(),
      ...getTraceStorageDiagnostics(),
      stage: 'storage_checked',
      flow: 'unknown',
      trace_origin: trace.origin,
      cookie_write_attempted: true,
      local_storage_write_attempted: true,
      trace_cookie_readback_present: writeResult.cookie,
      trace_local_storage_readback_present: writeResult.localStorage,
      landing_path: pathname,
    });
  }

  if (pathname.endsWith('/create-account') && trace) {
    sendUtmEvent('utm_pipeline_create_account_read', trace, {
      ...latestTouchDiagnostics(),
      ...getMarketingAttributionStorageDiagnostics(),
      ...getTraceStorageDiagnostics(),
      stage: 'create_account_read',
      flow: 'unknown',
      trace_origin: trace.origin,
    });
  }
  return trace;
}

export function observeAuthHandoff(flow) {
  try {
    if (!isBrowser()) return null;
    const { trace } = readTrace();
    if (!trace) return null;
    sendUtmEvent('utm_pipeline_auth_handoff_started', trace, {
      ...latestTouchDiagnostics(),
      ...getMarketingAttributionStorageDiagnostics(),
      ...getTraceStorageDiagnostics(),
      stage: 'auth_handoff_started',
      flow,
      trace_origin: trace.origin,
    });
    return trace;
  } catch {
    return null;
  }
}
